import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode, useRef } from 'react';
import { Store } from '../types/Store';

interface CachedStore {
    data: Store;
    timestamp: number;
}

interface StoreCacheContextType {
    getFromCache: (storeId: string | number) => Store | null;
    addToCache: (storeId: string | number, store: Store) => void;
    invalidateStore: (storeId: string | number) => void;
    invalidateAll: () => void;
    isCached: (storeId: string | number) => boolean;
}

const StoreCacheContext = createContext<StoreCacheContextType | undefined>(undefined);

export const useStoreCache = (): StoreCacheContextType => {
    const context = useContext(StoreCacheContext);
    if (!context) {
        throw new Error('useStoreCache must be used within a StoreCacheProvider');
    }
    return context;
};

interface StoreCacheProviderProps {
    children: ReactNode;
    maxAge?: number; // Durée de vie du cache en millisecondes (par défaut: 5 minutes)
}

export const StoreCacheProvider = ({ 
    children, 
    maxAge = 5 * 60 * 1000 // 5 minutes par défaut
}: StoreCacheProviderProps) => {
    const cacheRef = useRef<Map<string | number, CachedStore>>(new Map());

    const getFromCache = useCallback((entityId: string | number): Store | null => {
        const cached = cacheRef.current.get(entityId);
        
        if (!cached) return null;

        // Vérifier si le cache est encore valide
        const now = Date.now();
        if (now - cached.timestamp > maxAge) {
            // Cache expiré, le supprimer
            cacheRef.current.delete(entityId);
            return null;
        }

        return cached.data;
    }, [maxAge]);

    const addToCache = useCallback((entityId: string | number, store: Store) => {
        cacheRef.current.set(entityId, {
            data: store,
            timestamp: Date.now()
        });
    }, []);

    const invalidateStore = useCallback((entityId: string | number) => {
        cacheRef.current.delete(entityId);
    }, []);

    const invalidateAll = useCallback(() => {
        cacheRef.current.clear();
    }, []);

    const isCached = useCallback((entityId: string | number): boolean => {
        const cached = cacheRef.current.get(entityId);
        if (!cached) return false;

        const now = Date.now();
        return (now - cached.timestamp) <= maxAge;
    }, [maxAge]);

    const value = useMemo<StoreCacheContextType>(
        () => ({ getFromCache, addToCache, invalidateStore, invalidateAll, isCached }),
        [getFromCache, addToCache, invalidateStore, invalidateAll, isCached]
    );

    return (
        <StoreCacheContext.Provider value={value}>
            {children}
        </StoreCacheContext.Provider>
    );
};
